import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiAward,
  FiCheckCircle,
  FiUsers,
  FiBookOpen,
  FiGlobe,
  FiShield,
  FiTrendingUp,
  FiStar
} from 'react-icons/fi';
import Navbar from '../../components/navbar/navbar';
import UserNav from '../../components/navbar/user-nav';
import Footer from '../../components/footer';
import SEO from '../../components/SEO';
import { useUser } from '../../context/UserContext';

interface CertificacionBenefit {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const beneficiosCertificacion: CertificacionBenefit[] = [
  {
    icon: <FiAward size={28} />,
    title: 'Credencial Oficial',
    description: 'Obtén tu credencial AMPI que te identifica como profesional certificado ante clientes y colegas.'
  },
  {
    icon: <FiShield size={28} />,
    title: 'Respaldo Institucional',
    description: 'Cuentas con el respaldo de AMPI Nacional y acceso a defensa legal en caso de disputas.'
  },
  {
    icon: <FiUsers size={28} />,
    title: 'Red de Networking',
    description: 'Conecta con más de 16,000 profesionales inmobiliarios certificados en todo México.'
  },
  {
    icon: <FiBookOpen size={28} />,
    title: 'Capacitación Continua',
    description: 'Acceso a cursos, talleres y certificaciones especializadas para tu desarrollo profesional.'
  },
  {
    icon: <FiGlobe size={28} />,
    title: 'Alcance Internacional',
    description: 'Conexión con asociaciones inmobiliarias internacionales como NAR, FIABCI y CILA.'
  },
  {
    icon: <FiTrendingUp size={28} />,
    title: 'Mayor Confianza',
    description: 'Los clientes prefieren trabajar con asesores certificados, aumentando tus oportunidades de negocio.'
  }
];

const requisitos = [
  'Ser mayor de edad y contar con identificación oficial vigente',
  'Experiencia demostrable en el sector inmobiliario',
  'Carta de recomendación de un miembro AMPI (opcional pero recomendado)',
  'Aprobar el curso de inducción AMPI',
  'Comprometerse con el Código de Ética AMPI',
  'Cubrir la cuota de afiliación anual'
];

const procesoAfiliacion = [
  {
    paso: 1,
    titulo: 'Solicitud',
    descripcion: 'Completa el formulario de solicitud con tus datos profesionales y documentación.'
  },
  {
    paso: 2,
    titulo: 'Evaluación',
    descripcion: 'El comité de admisión revisa tu solicitud y experiencia profesional.'
  },
  {
    paso: 3,
    titulo: 'Capacitación',
    descripcion: 'Asiste al curso de inducción donde conocerás los estatutos y beneficios AMPI.'
  },
  {
    paso: 4,
    titulo: 'Certificación',
    descripcion: 'Recibe tu credencial oficial y comienza a disfrutar de todos los beneficios.'
  }
];

export default function Certificacion() {
  const { t } = useTranslation();
  const { user } = useUser();

  const certificacionSchema = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'Certificación AMPI Riviera Nayarit',
    description: 'Programa de certificación para profesionales inmobiliarios en Riviera Nayarit. Obtén tu credencial AMPI y únete a la red de asesores certificados más grande de México.',
    url: 'https://ampirivieranayarit.com/modulob/certificacion',
    provider: {
      '@type': 'Organization',
      name: 'AMPI Riviera Nayarit',
      url: 'https://ampirivieranayarit.com'
    },
    areaServed: {
      '@type': 'Place',
      name: 'Riviera Nayarit, México'
    }
  };

  return (
    <>
      <SEO
        title="Certificación AMPI - Profesionales Inmobiliarios"
        description="Obtén tu certificación AMPI y únete a la red de profesionales inmobiliarios más grande de México. Credencial oficial, capacitación continua y respaldo institucional."
        path="/modulob/certificacion"
        keywords="certificación ampi, certificado inmobiliario, credencial ampi, curso inmobiliario ampi, afiliación ampi, profesional inmobiliario certificado"
        schema={certificacionSchema}
      />

      {user ? (
        <UserNav
          nombreUsuario={user.nombre}
          nombreInmobiliaria={user.inmobiliaria_nombre}
        />
      ) : (
        <Navbar transparent={false} />
      )}

      {/* Hero Section */}
      <section
        className="py-5"
        style={{
          backgroundColor: '#0a2f27',
          backgroundImage: 'linear-gradient(135deg, #0a2f27 0%, #1a4a3f 100%)'
        }}
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <span
                className="badge mb-3 px-3 py-2"
                style={{ backgroundColor: 'rgba(2, 222, 127, 0.2)', color: '#02de7f' }}
              >
                <FiAward className="me-2" />
                Certificación Profesional
              </span>
              <h1 className="text-white fw-bold display-5 mb-3">
                Certifícate como Profesional Inmobiliario AMPI
              </h1>
              <p className="text-white-50 fs-5 mb-4">
                Únete a la red de más de 16,000 asesores certificados en México.
                Obtén tu credencial oficial y accede a beneficios exclusivos que
                impulsarán tu carrera.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <Link
                  to="/list-with-us"
                  className="btn btn-lg px-4"
                  style={{ backgroundColor: '#02de7f', color: '#0a2f27', fontWeight: 600 }}
                >
                  Solicitar Certificación
                </Link>
                <Link
                  to="/beneficios"
                  className="btn btn-lg btn-outline-light px-4"
                >
                  Ver Beneficios
                </Link>
              </div>
            </div>
            <div className="col-lg-5 text-center mt-5 mt-lg-0">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: '200px',
                  height: '200px',
                  backgroundColor: 'rgba(2, 222, 127, 0.15)'
                }}
              >
                <FiAward size={100} style={{ color: '#02de7f' }} />
              </div>
              <div className="mt-4">
                <div className="d-flex justify-content-center gap-4">
                  <div className="text-center">
                    <h3 className="text-white fw-bold mb-0">25+</h3>
                    <small className="text-white-50">Años de experiencia</small>
                  </div>
                  <div className="text-center">
                    <h3 className="text-white fw-bold mb-0">16K+</h3>
                    <small className="text-white-50">Miembros nacionales</small>
                  </div>
                  <div className="text-center">
                    <h3 className="text-white fw-bold mb-0">140+</h3>
                    <small className="text-white-50">En Riviera Nayarit</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ¿Qué es la Certificación AMPI? */}
      <section className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <h2 className="fw-bold mb-3" style={{ color: '#0a2f27' }}>
                ¿Qué es la Certificación AMPI?
              </h2>
              <p className="text-muted fs-5 mb-5">
                La Asociación Mexicana de Profesionales Inmobiliarios (AMPI) es la
                organización más grande y prestigiosa del sector inmobiliario en México.
                La certificación AMPI valida tu profesionalismo, ética y conocimientos,
                diferenciándote en un mercado competitivo.
              </p>
            </div>
          </div>

          {/* Beneficios Grid */}
          <div className="row g-4">
            {beneficiosCertificacion.map((beneficio, index) => (
              <div className="col-lg-4 col-md-6" key={index}>
                <div
                  className="card h-100 border-0 shadow-sm"
                  style={{ borderRadius: '12px' }}
                >
                  <div className="card-body p-4">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: 'rgba(2, 222, 127, 0.15)',
                        color: '#02de7f'
                      }}
                    >
                      {beneficio.icon}
                    </div>
                    <h5 className="fw-bold mb-2" style={{ color: '#0a2f27' }}>
                      {beneficio.title}
                    </h5>
                    <p className="text-muted mb-0">
                      {beneficio.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proceso de Certificación */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3" style={{ color: '#0a2f27' }}>
              Proceso de Certificación
            </h2>
            <p className="text-muted">
              Sigue estos simples pasos para obtener tu certificación AMPI
            </p>
          </div>

          <div className="row g-4">
            {procesoAfiliacion.map((item) => (
              <div className="col-lg-3 col-md-6" key={item.paso}>
                <div className="text-center">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                    style={{
                      width: '80px',
                      height: '80px',
                      backgroundColor: '#0a2f27',
                      color: '#02de7f',
                      fontSize: '2rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {item.paso}
                  </div>
                  <h5 className="fw-bold mb-2" style={{ color: '#0a2f27' }}>
                    {item.titulo}
                  </h5>
                  <p className="text-muted small">
                    {item.descripcion}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requisitos */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h2 className="fw-bold mb-4" style={{ color: '#0a2f27' }}>
                Requisitos para Certificarte
              </h2>
              <ul className="list-unstyled">
                {requisitos.map((requisito, index) => (
                  <li key={index} className="d-flex align-items-start mb-3">
                    <FiCheckCircle
                      className="text-success me-3 mt-1 flex-shrink-0"
                      size={20}
                    />
                    <span>{requisito}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/list-with-us"
                className="btn btn-primary mt-3"
              >
                Iniciar Solicitud
              </Link>
            </div>
            <div className="col-lg-6">
              <div
                className="p-5 rounded-3"
                style={{ backgroundColor: '#0a2f27' }}
              >
                <h4 className="text-white fw-bold mb-4">
                  <FiStar className="me-2" style={{ color: '#02de7f' }} />
                  Certificación ECI
                </h4>
                <p className="text-white-50 mb-4">
                  Además de la certificación básica, AMPI ofrece la certificación
                  <strong className="text-white"> ECI (Especialista Certificado en Inmuebles)</strong>,
                  un programa avanzado que te distingue como experto en el mercado.
                </p>
                <ul className="list-unstyled text-white-50">
                  <li className="mb-2">
                    <FiCheckCircle className="me-2" style={{ color: '#02de7f' }} />
                    Programa de formación especializada
                  </li>
                  <li className="mb-2">
                    <FiCheckCircle className="me-2" style={{ color: '#02de7f' }} />
                    Examen de certificación
                  </li>
                  <li className="mb-2">
                    <FiCheckCircle className="me-2" style={{ color: '#02de7f' }} />
                    Reconocimiento nacional e internacional
                  </li>
                </ul>
                <Link
                  to="/ver-taller"
                  className="btn btn-outline-light mt-3"
                >
                  Ver Cursos Disponibles
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonios / Social Proof */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3" style={{ color: '#0a2f27' }}>
              Lo que Dicen Nuestros Miembros
            </h2>
          </div>
          <div className="row g-4">
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FiStar
                        key={star}
                        className="me-1"
                        style={{ color: '#f59e0b', fill: '#f59e0b' }}
                      />
                    ))}
                  </div>
                  <p className="text-muted mb-3">
                    "La certificación AMPI me abrió puertas que antes parecían cerradas.
                    Mis clientes confían más en mí y he triplicado mis ventas."
                  </p>
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle me-3"
                      style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#e9ecef'
                      }}
                    />
                    <div>
                      <strong>María González</strong>
                      <small className="d-block text-muted">Asesora en Punta de Mita</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FiStar
                        key={star}
                        className="me-1"
                        style={{ color: '#f59e0b', fill: '#f59e0b' }}
                      />
                    ))}
                  </div>
                  <p className="text-muted mb-3">
                    "El networking con otros profesionales AMPI ha sido invaluable.
                    He cerrado operaciones gracias a referidos de colegas."
                  </p>
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle me-3"
                      style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#e9ecef'
                      }}
                    />
                    <div>
                      <strong>Carlos Ramírez</strong>
                      <small className="d-block text-muted">Asesor en Sayulita</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FiStar
                        key={star}
                        className="me-1"
                        style={{ color: '#f59e0b', fill: '#f59e0b' }}
                      />
                    ))}
                  </div>
                  <p className="text-muted mb-3">
                    "Los cursos de capacitación me mantienen actualizado en tendencias
                    del mercado. AMPI es una inversión en mi carrera."
                  </p>
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle me-3"
                      style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#e9ecef'
                      }}
                    />
                    <div>
                      <strong>Ana Martínez</strong>
                      <small className="d-block text-muted">Asesora en Nuevo Vallarta</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-5" style={{ backgroundColor: '#0a2f27' }}>
        <div className="container text-center">
          <h2 className="text-white fw-bold mb-3">
            ¿Listo para dar el siguiente paso en tu carrera?
          </h2>
          <p className="text-white-50 mb-4 fs-5">
            Únete a AMPI Riviera Nayarit y forma parte de la comunidad de
            profesionales inmobiliarios más prestigiosa de México.
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <Link
              to="/list-with-us"
              className="btn btn-lg px-5"
              style={{ backgroundColor: '#02de7f', color: '#0a2f27', fontWeight: 600 }}
            >
              Solicitar Certificación
            </Link>
            <Link
              to="/contact"
              className="btn btn-lg btn-outline-light px-5"
            >
              Contactar Asesora
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
